import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  exhaustMap,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';

import {
  GetMerchantAdminUsersResponse,
  PostMerchantAdminUserRequest,
  PutMerchantAdminUserRequest,
  User,
  UserResponseModel,
} from '@app/api/models';
import { UsersService } from '@app/api/services';

import { MerchantAccountActions } from './merchant-account.slice';

@Injectable()
export class MerchantAccountEffects {
  /* GET MERCHANT ADMINS */
  getMerchantAdmins$ = createEffect(
    (): Observable<GetMerchantAdminUsersResponse> =>
      this.actions$.pipe(
        ofType(MerchantAccountActions.getMerchantAdmins.trigger),
        exhaustMap(
          (): Observable<GetMerchantAdminUsersResponse> =>
            this.usersService.listMerchantUsers()
        ),
        tap((action: GetMerchantAdminUsersResponse) => {
          if (action) {
            this.store.dispatch(
              MerchantAccountActions.getMerchantAdmins.success(action)
            );
          }
        })
      ),
    { dispatch: false }
  );

  getUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MerchantAccountActions.getUser.trigger),
      switchMap((action) =>
        this.usersService.getUser({ userId: action.userId }).pipe(
          map((response: UserResponseModel) =>
            MerchantAccountActions.getUser.success({ user: response })
          ),
          catchError(() => of(MerchantAccountActions.getUser.failure()))
        )
      )
    )
  );

  /* CREATE MERCHANT ADMIN */
  createMerchantAdmin$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MerchantAccountActions.createMerchantAdmin.trigger),
        exhaustMap(
          (action: PostMerchantAdminUserRequest): Observable<User | void> =>
            this.usersService.addUserToMerchant({ body: action }).pipe(
              tap((successResponse: User) => {
                this.store.dispatch(
                  MerchantAccountActions.createMerchantAdmin.success(
                    successResponse
                  )
                );
                this.router.navigate(['/account/users']);
              }),
              catchError((errorResponse: HttpErrorResponse) =>
                of(
                  this.store.dispatch(
                    MerchantAccountActions.createMerchantAdmin.failure(
                      errorResponse.error
                    )
                  )
                )
              )
            )
        )
      ),
    { dispatch: false }
  );

  /* EDIT MERCHANT ADMIN */
  editMerchantAdmin$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MerchantAccountActions.editMerchantAdmin.trigger),
        exhaustMap(
          (action: {
            userId: string;
            request: PutMerchantAdminUserRequest;
          }): Observable<void> =>
            this.usersService
              .updateUser({ userId: action.userId, body: action.request })
              .pipe(
                tap(() => {
                  this.store.dispatch(
                    MerchantAccountActions.editMerchantAdmin.success()
                  );
                  this.router.navigate(['/account/users']);
                }),
                catchError((errorResponse: HttpErrorResponse) =>
                  of(
                    this.store.dispatch(
                      MerchantAccountActions.editMerchantAdmin.failure(
                        errorResponse.error
                      )
                    )
                  )
                )
              )
        )
      ),
    { dispatch: false }
  );

  /* REMOVE MERCHANT ADMIN */
  removeMerchantAdmin$ = createEffect(
    (): Observable<void> =>
      this.actions$.pipe(
        ofType(MerchantAccountActions.removeMerchantAdmin.trigger),
        exhaustMap(
          (action: { id: string }): Observable<void> =>
            this.usersService.deleteUser({ userId: action.id }).pipe(
              tap(() => {
                this.store.dispatch(
                  MerchantAccountActions.getMerchantAdmins.trigger()
                );
              })
            )
        )
      ),
    { dispatch: false }
  );

  constructor(
    private router: Router,
    private usersService: UsersService,
    private store: Store,
    private actions$: Actions
  ) {}
}
